import styled from '@emotion/styled'
import { Card, DESKTOP_BREAKPOINT, straitsXTheme, useModal, useTheme } from '@xfers/design-system'
import React, { Suspense, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { newStraitsXRoutes } from '~/biz/routes/config'
import {
  selectMerchantDetail,
  selectIsAccountLocked,
  selectUserId,
} from '~/biz/store/merchantInfo/selectors'
import Dpt2Tnc from '~/components/Dpt2Tnc'
import Dpt2LockedPage from '~/components/Dpt2Tnc/Dpt2LockedPage'
import { ErrorBoundary } from '~/components/ErrorBoundary/ErrorBoundary'
import { SessionTimeoutGuard } from '~/components/SessionTimeoutGuard'
import KillSwitchLockedPage from '~/components/KillSwitchLockedPage'
import PageLoader from '~/components/PageLoader'
import { PrivateRouteWrapper, RouteHooks } from '~/components/Route'
import SandboxBanner from '~/components/SandBoxBanner'
import StraitsXFooter from '~/components/StraitsXFooter'
import StraitsXNavigation from '~/components/StraitsXNavigation'
import StraitsXUserInfoBar from '~/components/StraitsXUserInfoBar'
import Tour from '~/components/Tour'
import { createGaEvent, createGaPageView } from '~/helpers/gaHelper'
import { useAutoAddPosthogIds } from '~/hooks/useAutoAddPosthogIds'
import { useShield } from '~/hooks/useShield'
import { SITE_TYPE } from '~/managers/ConfigManager'
import { ModeManager } from '~/managers/ModeManager'
import {
  useBizFlippers,
  useBizMintPhase1Flipper,
  useRootFlippers,
  useBizFlippersByBizId,
  useSelfServeRfiFlipper,
} from '~/store/flippers/hooks'
import {
  selectIsDashboardSelfServePhase1Enabled,
  selectIsBizEarnEnabled,
  selectIsSelfServeRfiEnabled,
} from '~/store/flippers/selectors'
import { selectUserBizId, selectUserEmail } from '~/store/route/selectors'
import { USER_ROLES } from '~/types'
import { useInitRoot } from './RootHooks'
import { PATHS } from './routes/paths'
import { selectHasBusinessAccount } from './store/accounts/selectors'
import { selectUserProfile } from './store/merchantInfo/selectors'
import { selectFeatureMatrixFactors, selectIsFiatWalletOnly } from './store/route/selectors'
import {
  selectIsCardDashboardAccessReady,
  selectShouldShowNewCardDashboard,
} from '~/store/cards/selectors'

export default () => {
  const { resetModal } = useModal()
  const location = useLocation()
  const email = useSelector(selectUserEmail)
  useAutoAddPosthogIds(SITE_TYPE.BIZ)
  useBizFlippers({ email })
  useSelfServeRfiFlipper()

  const isBusinessAccount = useSelector(selectHasBusinessAccount)
  const { merchantDetail } = useSelector(selectMerchantDetail)
  const userId = useSelector(selectUserId)
  const bizId = useSelector(selectUserBizId)

  const userProfile = useSelector(selectUserProfile)

  useRootFlippers()
  useShield(userId)
  useBizMintPhase1Flipper(bizId)
  useBizFlippersByBizId(bizId)

  const {
    isDeveloperToolEnabled: displayDevToolTab,
    isSandboxEnabled: displayModeSwitcher,
    role,
  } = useSelector(selectFeatureMatrixFactors)

  const displayPlatformToolsTab = useSelector(selectIsDashboardSelfServePhase1Enabled)
  const displayRfiManagementTab = useSelector(selectIsSelfServeRfiEnabled)

  const isFiatWalletOnly = useSelector(selectIsFiatWalletOnly)

  const isAccountLocked = useSelector(selectIsAccountLocked)

  const isEarnEnabled = useSelector(selectIsBizEarnEnabled)

  const shouldShowNewCardDashboard = useSelector(selectShouldShowNewCardDashboard)
  const isCardDashboardAccessReady = useSelector(selectIsCardDashboardAccessReady)

  // fetching application needed data
  const {
    displayGetStartedTab,
    isBizMintUIEnabled,
    isAccountStatementUIEnabled,
    isCardsDashboardEnabled,
    isInitiated,
    isKillSwitchActive,
    isStraitsxMenuDisabled,
    notifications,
    shouldRenderCardsDashboard,
    shouldRenderDpt2Tnc,
    shouldRenderManageAccounts,
  } = useInitRoot()

  // on sandbox<->production mode switched
  const onModeSwitched = () => {
    // TODO: think about better solution
    window.location.reload()
  }

  const isSandbox = ModeManager.isSandboxMode()

  useEffect(() => {
    const urlPath = location.pathname + location.search
    createGaPageView(urlPath)
    createGaEvent('URL', urlPath, 'Changed URL')
  }, [location])

  const isMintAccessible = isBizMintUIEnabled && !isFiatWalletOnly && !isAccountLocked
  const isOnboardingOperator = role === USER_ROLES.ONBOARDING_OPERATIONS

  const memoizedNav = useMemo(() => {
    return (
      <StraitsXNavigation
        displayEarnTab={
          !isOnboardingOperator && !isFiatWalletOnly && !!userProfile?.merchantId && isEarnEnabled
        }
        displayGetStartedTab={!isOnboardingOperator && displayGetStartedTab}
        displayMintTab={!isOnboardingOperator && isMintAccessible}
        displayDevToolTab={!isOnboardingOperator && displayDevToolTab && !displayPlatformToolsTab}
        displayPlatformToolsTab={!isOnboardingOperator && displayPlatformToolsTab}
        displayAccountStatementTab={!isOnboardingOperator && isAccountStatementUIEnabled}
        displayUserCardsTab={
          !isOnboardingOperator && isCardsDashboardEnabled && !shouldShowNewCardDashboard
        }
        displayCardDashboardTab={!isOnboardingOperator && shouldShowNewCardDashboard}
        displayRfiManagementTab={displayRfiManagementTab}
        disableStraitsxMenu={isStraitsxMenuDisabled}
        isSandbox={isSandbox}
        isBusinessAccount={isBusinessAccount}
        isOnboardingOperator={isOnboardingOperator}
      />
    )
  }, [
    isSandbox,
    displayGetStartedTab,
    isMintAccessible,
    isBusinessAccount,
    displayDevToolTab,
    displayPlatformToolsTab,
    displayRfiManagementTab,
    isAccountStatementUIEnabled,
    isFiatWalletOnly,
    isOnboardingOperator,
    userProfile?.merchantId,
    isCardsDashboardEnabled,
    shouldShowNewCardDashboard,
  ])

  if (!isInitiated) {
    return <PageLoader />
  }

  if (isKillSwitchActive) {
    return <KillSwitchLockedPage />
  }

  if (shouldRenderDpt2Tnc) {
    if (role === USER_ROLES.OWNER) {
      return <Dpt2Tnc siteType={SITE_TYPE.BIZ} />
    }
    return <Dpt2LockedPage />
  }

  return (
    <Root id="biz-root">
      <SessionTimeoutGuard />
      <RouteHooks resetModal={resetModal} />

      {isKillSwitchActive !== undefined && (
        <Container>
          <NavigationWrapper>{memoizedNav}</NavigationWrapper>
          <MainContainer isSandbox={isSandbox}>
            <ErrorBoundary key={location.pathname}>
              {isSandbox && <SandboxBanner />}
              <StraitsXUserInfoBar
                userInfo={{
                  fullName: userProfile?.fullName || '',
                  avatarUrl: userProfile?.avatarUrl || '',
                  displayname: merchantDetail?.businessName || '',
                }}
                isSandbox={isSandbox}
                notifications={notifications}
                displayGetStartedTab={displayGetStartedTab}
                displayMintTab={isMintAccessible}
                displayDevToolTab={displayDevToolTab && !displayPlatformToolsTab}
                displayPlatformToolsTab={displayPlatformToolsTab}
                displayAccountStatementTab={isAccountStatementUIEnabled}
                displayEarnTab={!isFiatWalletOnly && !!userProfile?.merchantId}
                displayModeSwitcher={displayModeSwitcher}
                displayUserCardsTab={isCardsDashboardEnabled && !shouldShowNewCardDashboard}
                displayCardDashboardTab={shouldShowNewCardDashboard}
                displayRfiManagementTab={displayRfiManagementTab}
                disableStraitsxMenu={isStraitsxMenuDisabled}
                isBusinessAccount={isBusinessAccount}
                onChangeModeCallback={onModeSwitched}
              />

              <Suspense fallback={<LoadingPage />}>
                <Routes>
                  {isOnboardingOperator && (
                    <Route
                      path={PATHS.LANDING_PAGE}
                      element={<Navigate to={PATHS.RFI_MANAGEMENT} replace />}
                    />
                  )}
                  {shouldRenderCardsDashboard && !isOnboardingOperator && (
                    <Route
                      path={PATHS.LANDING_PAGE}
                      element={
                        <Navigate
                          to={
                            shouldShowNewCardDashboard
                              ? PATHS.CARD_DASHBOARD_SETTLEMENT
                              : PATHS.USER_CARDS
                          }
                          replace
                        />
                      }
                    />
                  )}
                  <Route
                    path={PATHS.VERIFICATION}
                    element={<Navigate to={PATHS.LANDING_PAGE} replace />}
                  />
                  {shouldRenderManageAccounts && (
                    <Route
                      path={PATHS.MANAGE_ACCOUNT}
                      element={<Navigate to={PATHS.LANDING_PAGE} replace />}
                    />
                  )}
                  {!isMintAccessible && (
                    <>
                      <Route
                        path={PATHS.MINT}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.MINT_SETUP}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.MINT_DETAILS}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                    </>
                  )}
                  {isCardDashboardAccessReady && !shouldShowNewCardDashboard && (
                    <>
                      <Route
                        path={PATHS.CARD_DASHBOARD_SETTLEMENT}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.CARD_DASHBOARD_SETTLEMENT_DETAIL}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.CARD_DASHBOARD_MONITORING}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.CARD_DASHBOARD_MONITORING_TRANSACTION_DETAILS}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.CARD_DASHBOARD_MONITORING_CARD_DETAILS}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                      <Route
                        path={PATHS.CARD_DASHBOARD_MONITORING_CUSTOMER_DETAILS}
                        element={<Navigate to={PATHS.ERROR_403} replace />}
                      />
                    </>
                  )}
                  {newStraitsXRoutes.map((route, i) => (
                    <Route
                      key={i}
                      path={route.path}
                      element={
                        <PrivateRouteWrapper {...route}>
                          <route.component {...route} />
                        </PrivateRouteWrapper>
                      }
                    />
                  ))}
                </Routes>
              </Suspense>

              <StraitsXFooter />
            </ErrorBoundary>
          </MainContainer>
          <Tour />
        </Container>
      )}
    </Root>
  )
}

function LoadingPage() {
  const { spacing } = useTheme()

  return (
    <Page>
      <Card bodyStyle={{ padding: spacing.xl }}>
        <Skeleton style={{ width: '40%' }} />
        <Skeleton />
        <Skeleton />
      </Card>
    </Page>
  )
}

const Page = styled.div`
  padding: 16px;
  background: none;

  ${DESKTOP_BREAKPOINT} {
    background-color: ${straitsXTheme.color.background.secondaryNeutral};
    padding: 24px;
  }
`

const Root = styled.div`
  z-index: 0;
`

const Container = styled.div`
  background-size: 100%;
  background-color: ${straitsXTheme.color.background.secondaryNeutral};
  -webkit-font-smoothing: antialiased;
  display: flex;
`

const MainContainer = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: 73px;
  width: 100%;

  ${DESKTOP_BREAKPOINT} {
    padding-top: ${(props: { isSandbox: boolean }) => (props.isSandbox ? '73px' : 0)};
  }
`

const NavigationWrapper = styled.div`
  z-index: 1;
  display: none;

  ${DESKTOP_BREAKPOINT} {
    display: flex;
    width: 257px;
    flex-shrink: 0;
  }
`

const Skeleton = styled.div`
  display: block;
  width: 100%;
  height: 16px;

  margin: 10px;
  border-radius: 5px;
  background-size: 100wh;
  background-image: linear-gradient(
    90deg,
    ${straitsXTheme.color.base.line} 0%,
    ${straitsXTheme.color.background.secondaryNeutral} 50%,
    ${straitsXTheme.color.base.line} 100%
  );
  animation: shine-lines 1s infinite linear;

  @keyframes shine-lines {
    0% {
      background-position: -100px;
    }
    40%,
    100% {
      background-position: 140px;
    }
  }
`
